import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Input, Button, Typography } from "@material-tailwind/react";
import { ExclamationCircleIcon, CheckCircleIcon } from "@heroicons/react/24/solid";
import api from "../utils/axiosConfig";
import { toast } from "react-toastify";
import { useAuth } from "../auth/useAuth.hook";
import { ValidationMessages, validateVerificationCode } from "../utils/validationMessages";

interface VerifyCodeProps {
  agentId: number | null;
}

const VerifyCode: React.FC<VerifyCodeProps> = ({ agentId }) => {
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login, setAgentId } = useAuth();

  const apiUri = import.meta.env.VITE_API_URI as string;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(value);
    
    // Validar em tempo real
    if (value.length === 6) {
      const validation = validateVerificationCode(value);
      setCodeError(validation.message || null);
    } else if (value.length > 0) {
      setCodeError(`⏳ Digite os 6 dígitos (${value.length}/6)`);
    } else {
      setCodeError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar antes de enviar
    const validation = validateVerificationCode(code);
    if (!validation.isValid) {
      setCodeError(validation.message || null);
      toast.error(validation.message || ValidationMessages.GENERIC.REQUIRED_FIELD);
      return;
    }

    setIsLoading(true);
    try {
      const stringCode = code.toString();
      const response = await api.post(`${apiUri}login/verify-code`, {
        userId: agentId,
        code: stringCode,
      });
      if (response.status === 201) {
        const { accessToken } = response.data;
        login({ accessToken });
        if (agentId !== null) {
          setAgentId(agentId.toString());
        } else {
          const errorMsg = "❌ Erro ao validar o código. Tente novamente";
          setCodeError(errorMsg);
          toast.error(errorMsg);
          return;
        }
        setCodeError(null);
        toast.success("Código validado com sucesso! Bem-vindo!");
        navigate("/dashboard");
      }
      if (response.status === 404) {
        const errorMsg = ValidationMessages.LOGIN.CODE_INVALID;
        setCodeError(errorMsg);
        toast.error(errorMsg);
        return;
      }
    } catch (error: any) {
      let errorMsg = ValidationMessages.LOGIN.CODE_VERIFICATION_ERROR;
      
      if (error.response?.status === 404 || error.response?.status === 400) {
        errorMsg = ValidationMessages.LOGIN.CODE_INVALID;
      } else if (!navigator.onLine) {
        errorMsg = ValidationMessages.GENERIC.NETWORK_ERROR;
      }
      
      setCodeError(errorMsg);
      toast.error(errorMsg);
      console.error("Erro ao validar o código", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Card className="bg-transparent p-0" shadow={false} placeholder="">
        <form
          className="mt-5 space-y-5 w-full"
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col gap-3">
            <Typography
              variant="h6"
              className="text-sm font-medium text-gray-600"
              placeholder=""
            >
              Digite o código recebido via e-mail
            </Typography>
            <Input
              size="lg"
              placeholder="000000"
              value={code}
              maxLength={6}
              className={`focus:!ring-2 focus:!border-preppoBlue tracking-widest text-center text-2xl font-bold ${
                codeError && code.length > 0 && code.length < 6 ? "!border-yellow-500 focus:!ring-yellow-500" : ""
              } ${
                codeError && code.length === 6 ? "!border-red-500 focus:!ring-red-500" : "focus:!ring-preppoBlue"
              }`}
              onChange={handleChange}
              labelProps={{
                className: "before:content-none after:content-none",
              }}
              crossOrigin=""
            />
            {codeError && (
              <div className={`flex items-start gap-2 p-3 rounded-lg border ${
                code.length < 6 ? "bg-yellow-50 border-yellow-200" : "bg-red-50 border-red-200"
              }`}>
                <ExclamationCircleIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                  code.length < 6 ? "text-yellow-600" : "text-red-600"
                }`} />
                <Typography className={`text-xs leading-relaxed ${
                  code.length < 6 ? "text-yellow-700" : "text-red-700"
                }`} placeholder="">
                  {codeError}
                </Typography>
              </div>
            )}
          </div>
          <Button
            className="mt-6 bg-blue-500 hover:bg-blue-600 rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            fullWidth
            placeholder=""
            type="submit"
            disabled={isLoading || code.length !== 6}
          >
            {isLoading ? "Verificando..." : "Confirmar"}
          </Button>
        </form>
      </Card>
    </>
  );
}

export default VerifyCode;
